import {startTransition, useCallback, useEffect, useMemo, useState, type ReactNode} from 'react';
import {Box, Button, Skeleton, Stack, Typography} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import {SplitTabsPanel} from '@/components/common';
import {
    AuditProgramSectionVO,
    getDSectionVO,
    saveDSectionPage,
} from '@/services/auditService/auditProgramService';
import {
    D1_1_PAGE,
    D9_PAGE,
    D20_PAGE,
    D_PAGE_TITLES,
    D_SECTION_INDEX,
    D_SECTION_INDEX_LETTER,
    D_SECTION_INDEX_TITLE,
    D_SECTION_PAGES,
    getDProgrammePage,
    type D1_1Page,
    type D9Page,
    type D20Page,
    type DProgrammePage,
    type DSectionPageCode,
} from '@/pages/components/audit-program/d-section/constants';
import D1_1WorkingPaperView from '@/pages/components/audit-program/d-section/D1_1WorkingPaperView';
import D9ConfirmationView from '@/pages/components/audit-program/d-section/D9ConfirmationView';
import D20BlankSummaryView from '@/pages/components/audit-program/d-section/D20BlankSummaryView';
import G0ProgrammeView from '@/pages/components/audit-program/g-section/G0ProgrammeView';
import PageTabLabel from '@/pages/components/audit-program/PageTabLabel';
import SectionIndexView from '@/pages/components/audit-program/SectionIndexView';
import {useProgramSectionTemplates} from '@/pages/components/audit-program/templates/useProgramSectionTemplates';

export type DSectionViewProps = {
    companyId: number;
    annualId: number;
    companyName?: string | null;
    initialPage?: DSectionPageCode;
};

const DISPLAY_ONLY_PAGES = new Set<DSectionPageCode>(['Di']);

function toStringRecord(data?: Record<string, unknown>): Record<string, string> {
    if (!data) return {};
    return Object.fromEntries(
        Object.entries(data).map(([k, v]) => [k, v == null ? '' : String(v)]),
    );
}

function withHeaderDefaults(
    page: Record<string, string>,
    data: AuditProgramSectionVO,
    refCode: string,
): Record<string, string> {
    return {
        ...page,
        client: page.client || data.client || '',
        yearEnd: page.yearEnd || data.yearEnd || '',
        fileNo: page.fileNo || data.fileNo || '',
        preparedBy: page.preparedBy || data.preparedInitial || data.preparedBy || '',
        completedBy: page.completedBy || data.preparedInitial || data.preparedBy || '',
        reviewedBy: page.reviewedBy || data.reviewedInitial || data.reviewedBy || '',
        ref: page.ref || refCode,
    };
}

function isProgramme(code: DSectionPageCode): boolean {
    return getDProgrammePage(code) != null;
}

export default function DSectionView({
    companyId,
    annualId,
    companyName,
    initialPage = 'Di',
}: DSectionViewProps) {
    const [pageKey, setPageKey] = useState<DSectionPageCode>(initialPage);
    const [header, setHeader] = useState<AuditProgramSectionVO | null>(null);
    const [pageData, setPageData] = useState<Record<string, Record<string, string>>>({});
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [dirty, setDirty] = useState<Record<string, boolean>>({});
    const {getContent} = useProgramSectionTemplates('D');

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const data = await getDSectionVO(companyId, annualId);
            if (companyName && !data.client) {
                data.client = companyName;
            }
            setHeader(data);
            const pages: Record<string, Record<string, string>> = {};
            for (const code of D_SECTION_PAGES) {
                if (code === 'Di') {
                    pages[code] = toStringRecord(data.pages?.[code]);
                    continue;
                }
                pages[code] = withHeaderDefaults(toStringRecord(data.pages?.[code]), data, code);
            }
            setPageData(pages);
            setDirty({});
        } catch (e: unknown) {
            const msg = e instanceof Error ? e.message : 'Failed to load D Section';
            setError(msg);
            setHeader(null);
        } finally {
            setLoading(false);
        }
    }, [companyId, annualId, companyName]);

    useEffect(() => {
        void load();
    }, [load]);

    useEffect(() => {
        setPageKey(initialPage);
    }, [initialPage]);

    const handleFieldChange = useCallback((pageCode: DSectionPageCode, key: string, value: string) => {
        setPageData((prev) => ({
            ...prev,
            [pageCode]: {...(prev[pageCode] ?? {}), [key]: value},
        }));
        setDirty((prev) => ({...prev, [pageCode]: true}));
    }, []);

    const handleSave = useCallback(async () => {
        if (DISPLAY_ONLY_PAGES.has(pageKey) || !dirty[pageKey]) return;
        setSaving(true);
        setError(null);
        try {
            await saveDSectionPage(companyId, annualId, pageKey, pageData[pageKey] ?? {});
            setDirty((prev) => ({...prev, [pageKey]: false}));
        } catch (e: unknown) {
            const msg = e instanceof Error ? e.message : 'Failed to save';
            setError(msg);
        } finally {
            setSaving(false);
        }
    }, [companyId, annualId, dirty, pageKey, pageData]);

    const indexRows = useMemo(() => {
        const fromApi = getContent('Di') as {rows?: typeof D_SECTION_INDEX} | undefined;
        return fromApi?.rows?.length ? fromApi.rows : D_SECTION_INDEX;
    }, [getContent]);

    const tabs = useMemo(() => D_SECTION_PAGES.map((code) => {
        const programmeFallback = getDProgrammePage(code);
        const programmeContent = (getContent(code) ?? programmeFallback) as DProgrammePage | null;

        let content: ReactNode = null;
        if (header) {
            if (code === 'Di') {
                content = (
                    <SectionIndexView
                        header={header}
                        refCode="Di"
                        sectionLetter={D_SECTION_INDEX_LETTER}
                        sectionTitle={D_SECTION_INDEX_TITLE}
                        rows={indexRows}
                        onNavigate={(pageCode) => startTransition(() => setPageKey(pageCode as DSectionPageCode))}
                    />
                );
            } else if (code === 'D1.1') {
                content = (
                    <D1_1WorkingPaperView
                        header={header}
                        page={(getContent('D1.1') ?? D1_1_PAGE) as D1_1Page}
                        refCode="D1.1"
                        values={pageData['D1.1'] ?? {}}
                        onChange={(k, v) => handleFieldChange('D1.1', k, v)}
                    />
                );
            } else if (code === 'D9') {
                content = (
                    <D9ConfirmationView
                        header={header}
                        page={(getContent('D9') ?? D9_PAGE) as D9Page}
                        refCode="D9"
                        values={pageData.D9 ?? {}}
                        onChange={(k, v) => handleFieldChange('D9', k, v)}
                    />
                );
            } else if (code === 'D20') {
                content = (
                    <D20BlankSummaryView
                        header={header}
                        page={(getContent('D20') ?? D20_PAGE) as D20Page}
                        refCode="D20"
                        values={pageData.D20 ?? {}}
                        onChange={(k, v) => handleFieldChange('D20', k, v)}
                    />
                );
            } else if (isProgramme(code) && programmeContent) {
                content = (
                    <G0ProgrammeView
                        header={header}
                        page={programmeContent}
                        refCode={code}
                        values={pageData[code] ?? {}}
                        onChange={(k, v) => handleFieldChange(code, k, v)}
                    />
                );
            }
        }

        return {
            key: code,
            label: <PageTabLabel code={code} title={D_PAGE_TITLES[code]}/>,
            icon: code,
            content,
        };
    }), [header, pageData, handleFieldChange, indexRows, getContent]);

    if (loading) {
        return <Skeleton variant="rectangular" height={480} sx={{m: 2}}/>;
    }

    if (error && !header) {
        return (
            <Box sx={{p: 2}}>
                <Typography color="error">{error}</Typography>
            </Box>
        );
    }

    const saveDisabled = DISPLAY_ONLY_PAGES.has(pageKey) || !dirty[pageKey] || saving;

    return (
        <Box sx={{height: '100%', minHeight: 0, display: 'flex', flexDirection: 'column'}}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{px: 2, pt: 1, pb: 0.5}}>
                <Typography variant="subtitle1" fontWeight={600}>
                    D Section — Optional programmes
                </Typography>
                <Button
                    size="small"
                    variant="contained"
                    startIcon={<SaveIcon/>}
                    disabled={saveDisabled}
                    onClick={() => void handleSave()}
                >
                    {saving ? 'Saving…' : 'Save'}
                </Button>
            </Stack>
            {error ? (
                <Typography color="error" variant="body2" sx={{px: 2}}>{error}</Typography>
            ) : null}
            <Box sx={{flex: 1, minHeight: 0}}>
                <SplitTabsPanel
                    tabs={tabs}
                    selectedKey={pageKey}
                    onChange={(k) => setPageKey(k as DSectionPageCode)}
                    height="100%"
                    allowMaximize={false}
                    contentWrapperSx={{height: '100%', overflow: 'auto', p: 0}}
                    rightPaneSx={{height: '100%', overflow: 'hidden'}}
                />
            </Box>
        </Box>
    );
}
